import {
  collection,
  doc,
  onSnapshot,
  query,
  type FirestoreError,
  type QueryConstraint,
  type Unsubscribe,
} from "firebase/firestore";
import {
  DELIVERY_COLLECTION,
  toDelivery,
  type DeliveryMethod,
  type DeliveryOf,
} from "@gull/shared";
import { db } from "./firebase";

/**
 * Every delivery read in this app, so that asking for `"STORE"` or `"MAIL"` is the only choice a
 * page makes. A delivery's method and the collection it lives in are one fact — bound once in
 * `DELIVERY_COLLECTION` — and a page that names the collection itself is free to pair it with the
 * wrong method, which compiles and then quietly mislabels the rows.
 *
 * The web-SDK twin of `functions/src/data/deliveries.ts`, and of the identical helper in the admin
 * app. The plumbing is duplicated on purpose: each app queries through its own Firebase bundle, so
 * only the Firebase-free half — the pairing and the materialiser — is shared, from `@gull/shared`.
 */
export function subscribeDeliveries<M extends DeliveryMethod>(
  method: M,
  constraints: QueryConstraint[],
  onRows: (rows: DeliveryOf<M>[]) => void,
  onError?: (error: FirestoreError) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, DELIVERY_COLLECTION[method]), ...constraints),
    (snap) => onRows(snap.docs.map((document) => toDelivery(method, document.data()))),
    onError,
  );
}

/** One delivery by id, `null` once it is known not to exist. */
export function subscribeDelivery<M extends DeliveryMethod>(
  method: M,
  id: string,
  onRow: (row: DeliveryOf<M> | null) => void,
  onError?: (error: FirestoreError) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, DELIVERY_COLLECTION[method], id),
    (snap) => onRow(snap.exists() ? toDelivery(method, snap.data()) : null),
    onError,
  );
}
